import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, User, Settings, LogOut } from 'lucide-react';
import { companyInfo } from '../mock';
import { auth } from '../lib/firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { toast } from 'sonner';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // Handle scroll shadow
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auth listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return () => unsubscribe();
  }, []);

  // Close profile menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setShowProfileMenu(false);
      toast.success('Logged out successfully');
      navigate('/');
    } catch {
      toast.error('Failed to logout');
    }
  };

  const menuItems = [
    { label: 'Home', href: '#home' },
    { label: 'About', href: '#about' },
    { label: 'Services', href: '#services' },
    { label: 'Projects', href: '#projects' },
    { label: 'Tools', href: '#tools' },
    { label: 'Testimonials', href: '#testimonials' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <nav
      className={`nav-container sticky top-0 z-40 transition-all duration-300 ${
        isScrolled ? 'shadow-xl bg-bg-dark/95' : ''
      }`}
    >
      <div className="container mx-auto px-6 py-1">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-3">
            <img
              src={companyInfo.logo}
              alt={companyInfo.name}
              className="h-24 w-auto"
            />
          </a>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            {menuItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="text-white hover:text-accent-teal transition-all duration-300 font-medium text-base relative group"
              >
                {item.label}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent-teal transition-all duration-300 group-hover:w-full" />
              </a>
            ))}

            {/* Auth Section */}
            {user ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowProfileMenu((p) => !p)}
                  className="flex items-center gap-2 text-white hover:text-accent-teal transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-full bg-accent-teal/20 border-2 border-accent-teal flex items-center justify-center overflow-hidden">
                    {user.photoURL ? (
                      <img
                        src={user.photoURL}
                        alt="Profile"
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <User size={20} className="text-accent-teal" />
                    )}
                  </div>
                </button>

                {/* Profile Dropdown */}
                {showProfileMenu && (
                  <div
                    className="absolute right-0 mt-3 w-56 rounded-xl border border-white/10 shadow-2xl z-50"
                    style={{
                      backgroundColor: '#1f2023',
                      backdropFilter: 'none',
                    }}
                  >
                    <div className="py-2">
                      <div className="px-4 py-3 border-b border-white/10">
                        <p className="text-white text-sm font-semibold truncate">
                          {user.displayName || 'User'}
                        </p>
                        <p className="text-neutral-silver text-xs truncate">
                          {user.email}
                        </p>
                      </div>

                      <Link
                        to="/account-settings"
                        onClick={() => setShowProfileMenu(false)}
                        className="flex items-center gap-2 px-4 py-2 text-neutral-silver hover:text-accent-teal hover:bg-accent-teal/10 transition-colors"
                      >
                        <Settings size={16} />
                        <span>Account Settings</span>
                      </Link>

                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 w-full px-4 py-2 text-neutral-silver hover:text-accent-teal hover:bg-accent-teal/10 transition-colors"
                      >
                        <LogOut size={16} />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/tools/auth"
                className="px-6 py-2 bg-accent-teal text-white rounded-lg hover:bg-accent-purple transition-all duration-300 font-medium"
              >
                Login / Sign Up
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-3">
            {menuItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="block text-white hover:text-accent-teal transition-all duration-300 font-medium py-2 hover:pl-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.label}
              </a>
            ))}

            <div className="border-t border-white/10 pt-3 mt-3">
              {user ? (
                <>
                  <div className="px-2 py-2 mb-2">
                    <p className="text-white text-sm font-semibold truncate">
                      {user.displayName || 'User'}
                    </p>
                    <p className="text-neutral-silver text-xs truncate">
                      {user.email}
                    </p>
                  </div>

                  <Link
                    to="/account-settings"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2 text-white hover:text-accent-teal transition-all duration-300 font-medium py-2"
                  >
                    <Settings size={18} />
                    <span>Account Settings</span>
                  </Link>

                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      handleLogout();
                    }}
                    className="flex items-center gap-2 w-full text-white hover:text-accent-teal transition-all duration-300 font-medium py-2"
                  >
                    <LogOut size={18} />
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                <Link
                  to="/tools/auth"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-center px-6 py-2 bg-accent-teal text-white rounded-lg hover:bg-accent-purple transition-all duration-300 font-medium"
                >
                  Login / Sign Up
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
